import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useLang } from "@/i18n/LangContext";
import { Navbar } from "@/components/Navbar";
import { Copy, Users, Percent, Gift, Check, FileDown, History, Palette, QrCode, MessageCircle, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { QRCodeSVG } from "qrcode.react";

export default function DashboardPage() {
  const { token } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/client/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setData(res.data))
        .catch(() => toast.error("Failed to load dashboard"));
    }
  }, [token]);

  const copyCode = () => {
    if (data?.referral_code) {
      navigator.clipboard.writeText(data.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-[calc(6rem+env(safe-area-inset-top))] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const progressPercent = Math.min((data.discount / data.max_discount) * 100, 100);
  const remainingReferrals = Math.max(0, 6 - data.referral_count);

  const downloadCarePDF = () => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const w = 210, h = 297;

    // Background
    pdf.setFillColor(5, 5, 5);
    pdf.rect(0, 0, w, h, "F");

    // Subtle purple glow
    pdf.setFillColor(15, 8, 18);
    pdf.rect(20, 20, w - 40, h - 40, "F");

    // Double border
    pdf.setDrawColor(217, 70, 239);
    pdf.setLineWidth(0.5);
    pdf.rect(10, 10, w - 20, h - 20);
    pdf.setDrawColor(100, 30, 110);
    pdf.setLineWidth(0.2);
    pdf.rect(13, 13, w - 26, h - 26);

    // Corner accents
    const c = 5;
    pdf.setDrawColor(217, 70, 239);
    pdf.setLineWidth(0.8);
    [[10, 10, 10 + c, 10, 10, 10 + c], [w - 10, 10, w - 10 - c, 10, w - 10, 10 + c], [10, h - 10, 10 + c, h - 10, 10, h - 10 - c], [w - 10, h - 10, w - 10 - c, h - 10, w - 10, h - 10 - c]].forEach(([x1, y1, x2, y2, x3, y3]) => {
      pdf.line(x2, y2, x1, y1);
      pdf.line(x1, y1, x3, y3);
    });

    // Header
    pdf.setTextColor(217, 70, 239);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("P U R P L E   I N K", w / 2, 28, { align: "center" });

    pdf.setTextColor(163, 163, 163);
    pdf.setFontSize(8);
    pdf.text("T A T T O O   S T U D I O", w / 2, 34, { align: "center" });

    // Divider
    pdf.setDrawColor(217, 70, 239);
    pdf.setLineWidth(0.3);
    pdf.line(40, 38, w - 40, 38);

    // Diamond accent
    pdf.setFillColor(217, 70, 239);
    const dx = w / 2, dy = 43;
    pdf.triangle(dx, dy - 2.5, dx + 2.5, dy, dx, dy + 2.5, "F");
    pdf.triangle(dx, dy - 2.5, dx - 2.5, dy, dx, dy + 2.5, "F");

    // Title
    pdf.setTextColor(229, 229, 229);
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(12);
    pdf.text("Tattoo Aftercare Guide", w / 2, 54, { align: "center" });

    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.text("CARE INSTRUCTIONS", w / 2, 64, { align: "center" });

    // Client name
    if (data?.name) {
      pdf.setTextColor(217, 70, 239);
      pdf.setFontSize(11);
      pdf.text(`Prepared for ${data.name}`, w / 2, 72, { align: "center" });
    }

    // Divider
    pdf.setDrawColor(100, 30, 110);
    pdf.setLineWidth(0.2);
    pdf.line(30, 77, w - 30, 77);

    const sections = [
      { title: "FIRST 24 HOURS", items: [
        "Keep the bandage on for 2-4 hours after your session.",
        "Gently wash with lukewarm water and fragrance-free soap.",
        "Pat dry with a clean paper towel \u2014 never rub.",
        "Apply a thin layer of recommended aftercare ointment.",
      ]},
      { title: "DAYS 2-14 (HEALING PHASE)", items: [
        "Wash your tattoo 2-3 times daily with mild, fragrance-free soap.",
        "Apply a thin layer of unscented moisturizer after each wash.",
        "Do NOT pick, scratch, or peel any flaking skin.",
        "Avoid submerging in water (pools, baths, ocean).",
        "Wear loose, breathable clothing over the tattooed area.",
      ]},
      { title: "WEEKS 2-4 (CONTINUED CARE)", items: [
        "Keep moisturizing the tattoo regularly.",
        "Avoid direct sunlight \u2014 UV rays can fade fresh ink.",
        "Do not apply sunscreen until the tattoo is fully healed.",
        "Avoid heavy exercise that causes excessive sweating on the area.",
      ]},
      { title: "LONG-TERM CARE", items: [
        "Always apply SPF 50+ sunscreen on healed tattoos when exposed to sun.",
        "Keep your skin hydrated \u2014 moisturized skin preserves ink vibrancy.",
        "Avoid prolonged soaking in chlorinated or salt water.",
        "Schedule a touch-up session if needed (within the first year).",
      ]},
      { title: "WHEN TO CONTACT US", items: [
        "Excessive redness, swelling, or warmth after 48 hours.",
        "Pus or unusual discharge from the tattoo.",
        "Fever or signs of infection.",
        "Severe allergic reaction (rare but possible).",
      ]},
    ];

    let y = 84;
    const leftMargin = 25;
    const textWidth = w - 50;

    sections.forEach((section) => {
      pdf.setTextColor(217, 70, 239);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.text(section.title, leftMargin, y);
      y += 5;

      pdf.setTextColor(200, 200, 200);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      section.items.forEach((item) => {
        const lines = pdf.splitTextToSize(`\u2022  ${item}`, textWidth);
        lines.forEach((line) => {
          if (y > h - 30) return;
          pdf.text(line, leftMargin + 3, y);
          y += 4;
        });
      });
      y += 4;
    });

    // Footer
    pdf.setDrawColor(100, 30, 110);
    pdf.setLineWidth(0.2);
    pdf.line(30, h - 28, w - 30, h - 28);

    pdf.setTextColor(163, 163, 163);
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(7);
    pdf.text("Purple INK Studio \u00B7 Your art, our care.", w / 2, h - 22, { align: "center" });
    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(6);
    pdf.text(`Generated on ${new Date().toLocaleDateString()}`, w / 2, h - 17, { align: "center" });

    pdf.save(`PINK_Tattoo_Care_${(data?.name || "Client").replace(/\s+/g, "_")}.pdf`);
    toast.success("Tattoo care instructions downloaded!");
  };

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay" data-testid="dashboard-page">
      <Navbar />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-24 pb-16">
        <p
          className="text-xs tracking-[0.3em] uppercase font-bold text-purple-400 mb-3"
          style={{ fontFamily: "'Cinzel Decorative', serif" }}
        >
          {t("dash_subtitle")}
        </p>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4" data-testid="dashboard-title">
          {t("dash_title")}
        </h1>

        {/* Assigned Artist */}
        {data.assigned_artist_name && (
          <div className="flex items-center gap-2 mb-12" data-testid="assigned-artist">
            <Palette className="w-4 h-4 text-pink-400" />
            <p className="text-sm text-neutral-400">
              {t("dash_artist")}: <span className="text-pink-300 font-semibold">{data.assigned_artist_name}</span>
            </p>
          </div>
        )}
        {!data.assigned_artist_name && <div className="mb-12" />}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* Discount Card */}
          <div
            className="flex flex-col gap-2 p-6 bg-white/5 rounded-2xl border border-white/5 relative overflow-hidden group hover:border-purple-500/30 transition-colors duration-500"
            data-testid="discount-card"
          >
            <div className="absolute top-0 right-0 w-32 h-32 opacity-20 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(217, 70, 239, 0.3), transparent 70%)" }}
            />
            <div className="w-10 h-10 flex items-center justify-center bg-purple-500/10 rounded-lg mb-2">
              <Percent className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-xs tracking-wider uppercase text-neutral-500">{t("dash_discount")}</p>
            <p className="text-4xl font-extrabold text-purple-400 neon-text-purple" data-testid="discount-value">
              {data.discount}%
            </p>
          </div>

          {/* Referral Count Card */}
          <div
            className="flex flex-col gap-2 p-6 bg-white/5 rounded-2xl border border-white/5 relative overflow-hidden group hover:border-pink-500/30 transition-colors duration-500"
            data-testid="referral-count-card"
          >
            <div className="absolute top-0 right-0 w-32 h-32 opacity-20 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(219, 39, 119, 0.3), transparent 70%)" }}
            />
            <div className="w-10 h-10 flex items-center justify-center bg-pink-500/10 rounded-lg mb-2">
              <Users className="w-5 h-5 text-pink-400" />
            </div>
            <p className="text-xs tracking-wider uppercase text-neutral-500">{t("dash_referrals_made")}</p>
            <p className="text-4xl font-extrabold text-pink-400 neon-text-pink" data-testid="referral-count-value">
              {data.referral_count}
            </p>
          </div>

          {/* Remaining Card */}
          <div
            className="flex flex-col gap-2 p-6 bg-white/5 rounded-2xl border border-white/5 relative overflow-hidden group hover:border-purple-500/30 transition-colors duration-500"
            data-testid="remaining-card"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-purple-500/10 rounded-lg mb-2">
              <Gift className="w-5 h-5 text-purple-400" />
            </div>
            <p className="text-xs tracking-wider uppercase text-neutral-500">{t("dash_referrals_remaining")}</p>
            <p className="text-4xl font-extrabold text-neutral-300" data-testid="remaining-value">
              {remainingReferrals}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="glass-card p-8 rounded-xl mb-8" data-testid="progress-section">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold">{t("dash_progress")}</p>
            <p className="text-xs text-neutral-500">
              {data.discount}% / {data.max_discount}%
            </p>
          </div>
          <div className="h-3 bg-neutral-800 rounded-full overflow-hidden" data-testid="progress-bar">
            <div
              className="h-full rounded-full shadow-[0_0_10px_rgba(217,70,239,0.5)] transition-all duration-1000 ease-out"
              style={{
                width: `${progressPercent}%`,
                background: "linear-gradient(to right, #7e22ce, #db2777)",
              }}
              data-testid="progress-fill"
            />
          </div>
          <p className="text-xs text-neutral-500 mt-3">
            {remainingReferrals > 0
              ? t("dash_progress_text", { n: remainingReferrals })
              : t("dash_referrals_remaining") + " 0"}
          </p>
        </div>

        {/* Referral Code */}
        <div
          className="relative overflow-hidden bg-neutral-900/50 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-500 p-8 rounded-xl"
          data-testid="referral-code-section"
        >
          <div
            className="absolute inset-0 opacity-10 pointer-events-none bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1613327986042-63d4425a1a5d?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            }}
          />
          <div className="relative z-10">
            <p className="text-sm font-semibold mb-2">{t("dash_referral_code")}</p>
            <p className="text-xs text-neutral-500 mb-4">
              {t("dash_referral_share")}
            </p>
            <div className="flex items-center gap-3">
              <div className="bg-black/60 border border-purple-500/30 px-6 py-3 rounded-lg flex-1 max-w-xs">
                <span
                  className="text-lg font-bold text-purple-300 tracking-[0.15em]"
                  data-testid="referral-code-display"
                >
                  {data.referral_code}
                </span>
              </div>
              <button
                onClick={copyCode}
                className="w-12 h-12 flex items-center justify-center bg-purple-600 hover:bg-purple-500 border border-purple-400 text-white transition-all duration-300 rounded-lg shadow-[0_0_15px_rgba(192,38,211,0.3)] hover:shadow-[0_0_25px_rgba(192,38,211,0.6)]"
                data-testid="copy-referral-btn"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(t("dash_share_msg", { code: data.referral_code }))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 text-[#25D366] text-xs font-semibold rounded-lg transition-all duration-300"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                {t("dash_share_whatsapp")}
              </a>
              <a
                href={`sms:?body=${encodeURIComponent(t("dash_share_msg", { code: data.referral_code }))}`}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-400 text-xs font-semibold rounded-lg transition-all duration-300"
                data-testid="share-sms-btn"
              >
                <Smartphone className="w-4 h-4" />
                {t("dash_share_sms")}
              </a>
            </div>
          </div>
        </div>

        {/* Tattoo Care Instructions */}
        <div
          className="relative overflow-hidden bg-gradient-to-br from-purple-900/20 to-neutral-900/50 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500 p-8 rounded-xl mt-8"
          data-testid="tattoo-care-section"
        >
          <div className="absolute top-0 right-0 w-40 h-40 opacity-10 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(217, 70, 239, 0.4), transparent 70%)" }}
          />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold mb-1">{t("dash_care_title")}</p>
              <p className="text-xs text-neutral-500">
                {t("dash_care_desc")}
              </p>
            </div>
            <button
              onClick={downloadCarePDF}
              className="flex items-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold rounded-lg transition-all duration-300 shadow-[0_0_15px_rgba(192,38,211,0.3)] hover:shadow-[0_0_25px_rgba(192,38,211,0.6)] whitespace-nowrap"
              data-testid="download-care-pdf-btn"
            >
              <FileDown className="w-4 h-4" />
              {t("dash_care_btn")}
            </button>
          </div>
        </div>

        {/* QR Code Section */}
        <div
          className="relative overflow-hidden bg-gradient-to-br from-neutral-900/80 to-purple-950/20 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500 p-8 rounded-xl mt-8"
          data-testid="qr-code-section"
        >
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <QrCode className="w-5 h-5 text-purple-400" />
                <p className="text-sm font-semibold">{t("dash_qr_title")}</p>
              </div>
              <p className="text-xs text-neutral-500 mb-1">
                {t("dash_qr_desc")}
              </p>
              {data.discount > 0 && (
                <p className="text-xs text-purple-400 mt-2">
                  {t("dash_qr_ready")}: <span className="font-bold text-purple-300">{data.discount}%</span>
                </p>
              )}
            </div>
            <div className="bg-white p-4 rounded-xl shadow-[0_0_30px_rgba(192,38,211,0.2)]" data-testid="qr-code-display">
              <QRCodeSVG
                value={JSON.stringify({ client_id: data.client_id, qr_token: data.qr_token })}
                size={160}
                bgColor="#ffffff"
                fgColor="#1a1a1a"
                level="H"
              />
            </div>
          </div>
        </div>

        {/* Claiming History */}
        {data.claim_history && data.claim_history.length > 0 && (
          <div className="glass-card p-8 rounded-xl mt-8" data-testid="claim-history-section">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold">{t("dash_claim_title")}</h3>
            </div>
            <p className="text-xs text-neutral-500 mb-4">{t("dash_claim_desc")}</p>
            <div className="space-y-3">
              {data.claim_history.map((claim, idx) => (
                <div
                  key={claim.id || idx}
                  className="flex items-center justify-between py-3 border-b border-neutral-800 last:border-0"
                  data-testid={`claim-item-${idx}`}
                >
                  <div>
                    <span className="text-sm font-semibold text-emerald-400">{t("dash_claim_item", { pct: claim.discount_claimed })}</span>
                    {claim.notes && <p className="text-xs text-neutral-500 mt-0.5">{claim.notes}</p>}
                  </div>
                  <span className="text-xs text-neutral-500">
                    {new Date(claim.claimed_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral History */}
        {data.referrals && data.referrals.length > 0 && (
          <div className="glass-card p-8 rounded-xl mt-8" data-testid="referral-history">
            <h3 className="text-lg font-semibold mb-4">{t("dash_referral_title")}</h3>
            <div className="space-y-3">
              {data.referrals.map((ref, idx) => (
                <div
                  key={ref.id || idx}
                  className="flex items-center justify-between py-3 border-b border-neutral-800 last:border-0"
                  data-testid={`referral-item-${idx}`}
                >
                  <span className="text-sm text-neutral-300">{ref.new_client_name}</span>
                  <span className="text-xs text-neutral-500">
                    {new Date(ref.date_used).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
